/**
 * Activity (L3, TRACEABILITY_PLAN §1a): feed of the user's journal action history
 * ("you wrote", "you edited", "you moved to the trash",
 * "you undid…"). It reads the local `activity_recent` bucket (~90 days); deep
 * history lives in the API. PERSONAL scope: SQLite only has the user's data.
 *
 * Each revertible row offers "Deshacer": it calls `POST /api/activity/:id/revert`
 * (server-authoritative compensating write, validated by the Rust tests);
 * the result returns through PowerSync and refreshes this same feed (live query).
 */
import { createRoute } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { History } from "@fleet/icons";
import { ACTIVITY_FEED } from "@fleet/plugins/activity";
import { useKernelService } from "@fleet/kernel";
import {
  type ActivityRow,
  type components,
  groupActivityByBatch,
  isOfflineEdit,
  isRevertibleActivity,
} from "@journal/shared";
import { appRoute } from "./route";
import { PageContainer, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { ActivityItem } from "@/features/activity/components/activity-item";
import { activitySentence, formatRelativeTime } from "@/features/activity/lib/activity-text";

function ActivityPage() {
  const { t, locale } = useI18n();
  const feed = useKernelService(ACTIVITY_FEED);

  const rows = (): ActivityRow[] => feed()?.rows() ?? [];

  // ID of the row whose compensating write is in progress (only one at a time).
  const [revertingId, setRevertingId] = createSignal<string | null>(null);

  const revert = createApiMutation<
    string,
    components["schemas"]["RevertResponse"],
    components["schemas"]["ErrorModel"]
  >(
    async (id) => api.POST("/api/activity/{id}/revert", { params: { path: { id } } }),
    () => ({
      onSuccess: () => {
        // The feed is a live query over `activity_recent`: the `reverted` row
        // appears by itself when the write returns through PowerSync. Nothing needs
        // to be invalidated.
        showToast(t(msg`Change undone.`), "success");
      },
      onError: () => {
        showToast(t(msg`Couldn't undo that change.`), "error");
      },
      onSettled: () => setRevertingId(null),
    }),
  );

  const handleRevert = (id: string) => {
    if (revertingId()) {
      return;
    }
    setRevertingId(id);
    revert.mutate(id);
  };

  return (
    <PageContainer maxWidth="2xl" class="flex flex-col gap-4 pb-16">
      <header class="flex flex-col gap-1 pt-2">
        <h1 class="flex items-center gap-2 text-xl font-bold text-foreground">
          <History class="h-5 w-5 text-muted-foreground" />
          {t(msg`Activity`)}
        </h1>
        <p class="text-xs text-muted-foreground">
          {t(msg`A private log of the changes you make across your journal.`)}
        </p>
      </header>

      <Show
        when={!(feed()?.isLoading() ?? true)}
        fallback={
          <div class="flex flex-col gap-3">
            <Skeleton class="h-16 w-full rounded-2xl" />
            <Skeleton class="h-16 w-full rounded-2xl" />
            <Skeleton class="h-16 w-full rounded-2xl" />
          </div>
        }
      >
        <Show
          when={rows().length > 0}
          fallback={
            <div data-testid="activity-empty" class="flex flex-1">
              <EmptyState
                icon={History}
                title={t(msg`No activity yet`)}
                description={t(msg`As you write and edit entries, your history will appear here.`)}
              />
            </div>
          }
        >
          <section class="flex flex-col gap-2" data-testid="activity-list">
            <For each={groupActivityByBatch(rows())}>
              {(group) => (
                <For each={group.entries}>
                  {(row) => {
                    const { descriptor, values } = activitySentence(row);
                    return (
                      <ActivityItem
                        sentence={t(descriptor, values)}
                        timeLabel={formatRelativeTime(row.server_ts, locale())}
                        native={row.platform === "native"}
                        offline={isOfflineEdit(row)}
                        offlineLabel={t(msg`Done offline`)}
                        revertible={isRevertibleActivity(row)}
                        reverting={revertingId() === row.id}
                        undoLabel={t(msg`Undo`)}
                        onRevert={() => {
                          handleRevert(row.id);
                        }}
                      />
                    );
                  }}
                </For>
              )}
            </For>
          </section>

          <Show when={feed()?.hasMore()}>
            <div class="flex justify-center pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={feed()?.isLoadingOlder()}
                onClick={() => void feed()?.loadOlder()}
                data-testid="activity-load-older"
              >
                {feed()?.isLoadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
              </Button>
            </div>
          </Show>
        </Show>
      </Show>
    </PageContainer>
  );
}

export const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/activity",
  component: ActivityPage,
});
